import { defineComponent as _defineComponent } from 'vue'
import WidgetDefinition, {
  ParamValues,
} from '@/models/Charts/widgetDefinition';
import { findWidgetType, WidgetEnum } from '@/models/enums/WidgetEnum';
import { WidgetType } from '@/models/enums/WidgetType';
import { DataRetriever } from '@/models/Charts/abstract/chartGenerator';
import { FilterTimeSpanEnum } from '@/models/enums/FilterTimeSpanEnum';
import { RequestCanceledError } from '@/services/requestCanceledError';
import { Logger } from '@/utils/logger';
import WidgetFactory from '@/models/Charts/widgetFactory';
import { filterChanged } from '@/models/metrics/widgetFilters';
import { useI18n } from '@/composables/i18n';
import { customersService } from '@/services/tenants.service';
import WidgetHelp from '../WidgetHelp.vue';
import { DynamicModal } from './common';
import QuickSearchItem from '@/models/metrics/quickSearchItem';
import { downloadCsv } from '@/services/metricsDownloadService';
import WidgetCard from './WidgetCard.vue';
import MapWidget from '@/components/Charts/MapWidget.vue';
import {
  AsyncComponent,
  computed,
  defineAsyncComponent,
  getCurrentInstance,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  shallowRef,
  watch,
} from 'vue';
import { currentUser } from '@/utils/permissionUtils';
import { useInterval } from '@/composables/misc';
import { modalService } from '@/library-services/modal.service';
import { useRoute } from '@/composables/router';

/** @deprecated THIS COMPONENT IS FROZEN **/
const __default__ = {};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'SingleWidgetContainer',
  props: {
    widgetDefinition: null,
    selfManagedFilters: { type: Boolean, default: false },
    widgetHelpPosition: { default: 'is-bottom-right' },
    autorefresh: { type: Boolean, default: true },
    isWidgetSelector: { type: Boolean, default: false },
    customerNo: null,
    threeDotsInnerPosition: { default: 'is-right' }
  },
  emits: ["param-update", "change"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'param-update'): void;
  (e: 'change', value: WidgetDefinition): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { widgetDefinition: WidgetDefinition, selfManagedFilters: boolean, widgetHelpPosition: string, autorefresh: boolean, isWidgetSelector: boolean, customerNo?: string, threeDotsInnerPosition: string };

const CameraView = defineAsyncComponent(
  () => import('@/components/CameraView.vue'),
);
const Chart = defineAsyncComponent(
  () => import('@/components/Charts/Chart.vue'),
);

// KPI widgets
const NumberOfBendsWidget = defineAsyncComponent(
  () => import('@/components/bend/NumberOfBendsWidget.vue'),
);
const PeakHourlyBendingWidget = defineAsyncComponent(
  () => import('@/components/bend/PeakHourlyBendingWidget.vue'),
);
const BendProductivityKpiWidget = defineAsyncComponent(
  () => import('@/components/bend/BendProductivityKpiWidget.vue'),
);
const BendProductionOutputWidget = defineAsyncComponent(
  () => import('@/components/bend/BendProductionOutputWidget.vue'),
);


provide('widgetDefinition', props.widgetDefinition);



const i18n = useI18n();
const app = getCurrentInstance();
const route = useRoute();

/*
 * Injects are not natively reactive in Vue 2, but the @ProvideReactive and @InjectReactive
 * implement a workaround. If an object is injected, and the values of that object change, you can
 * kind of implement a reactive injection. This object is __reactiveInject__, and it's where
 * the technologyTenantIdDh and supportTenantIdDh are actually being injected.
 */
const injectReactive = inject<any>('__reactiveInject__', {});
// These injects will work again after migrating to Vue 3.
// const technologyTenantIdDh: number | null = inject('technologyTenantIdDh', null);
// const supportTenantIdDh: number | null = inject('supportTenantIdDh', null);

const mapWidgetRef = ref<InstanceType<typeof MapWidget>>();

const dataRetriever = shallowRef<DataRetriever<any>>();
const data = shallowRef<any>(null);
const isFetchingForTheFirstTime = ref(true);
const loading = ref(true);
const paramValues = ref<Record<string, any>>({});

if (props.autorefresh) {
  useInterval(update, 40);
}

onMounted(async () => {
  initializeParamValuesIfDefined();

  dataRetriever.value = WidgetFactory.createDataRetriever(
    props.widgetDefinition.widget,
    customerIdDh.value,
  );
  await update();
});

async function getData() {
  if (props.isWidgetSelector) {
    return dataRetriever.value?.getMockData();
  } else {
    dataRetriever.value?.abort();
    return dataRetriever.value?.getData(
      deviceIds.value,
      props.widgetDefinition.shifts,
      props.widgetDefinition.timeFilter ?? FilterTimeSpanEnum.None,
      props.widgetDefinition.axisSpan,
      paramValues.value,
      props.customerNo,
    );
  }
}

async function update() {
  if (widgetType.value === WidgetType.CameraView) {
    return;
  } else if (widgetType.value === WidgetType.Map) {
    // Waiting for the next tick when the map is updated helps to let the component fully load.
    // This in turn means that Leaflet will correctly run fitBounds always, and that when switching
    // between composites the map is also fully mounted.
    nextTick(() => {
      mapWidgetRef.value?.update();
    });
    return;
  }
  const widgetDefinitionInternal = props.widgetDefinition;
  loading.value = true;
  let dataInternal: any = null;
  try {
    dataInternal = await getData();
  } catch (e) {
    // Avoid the chart being hidden while the new response arrives
    // (We throw an exception when a request is aborted).
    if (!(e instanceof RequestCanceledError)) {
      data.value = null; // To show 'no data'
      Logger.error(`Error loading data for '${widgetTitle.value}' chart.`, e);
    }
  } finally {
    if (!hasWidgetDefinitionChanged(widgetDefinitionInternal)) {
      // This is checked to avoid a race condition.
      // This race condition could happen when a widget definition is changed before the
      // request has been completed. This function would be called again before the previous
      // call has ended, and the loading.value assignment in line 461 and the one below would
      // conflict. For example, when quickly clicking the switch in WCC Cut.
      data.value = dataInternal ?? null; // To show 'no data'
      loading.value = false;
      isFetchingForTheFirstTime.value = false;
    }
  }
}

function initializeParamValuesIfDefined() {
  paramValues.value = {
    ...generateParamValuesWithDefaults(),
    ...props.widgetDefinition.paramValues,
    pageNumber: 1,
  };
}

function generateParamValuesWithDefaults() {
  return Object.entries(props.widgetDefinition.params ?? {})
    .map(([paramName, paramInfo]) => ({ [paramName]: paramInfo.defaultValue }))
    .reduce((a, b) => ({ ...a, ...b }), {});
}

function hasWidgetDefinitionChanged(
  widgetDefinition: WidgetDefinition,
): boolean {
  return (
    props.widgetDefinition.widget !== widgetDefinition.widget ||
    filterChanged(props.widgetDefinition, widgetDefinition)
  );
}

function onParamUpdated(paramValuesInternal: ParamValues): void {
  let widgetDefinitionInternal = props.widgetDefinition;

  Object.entries(paramValuesInternal).forEach(([paramName, paramValue]) => {
    widgetDefinitionInternal = widgetDefinitionInternal.updateParamValue(
      paramName,
      paramValue,
    );
  });
  // Important: We don't want to call update() here. It'll be triggered by the
  // 'change' event through watchChanges.
  emit('param-update');
  emit('change', widgetDefinitionInternal);
}

function onPageChange(page: number) {
  paramValues.value.pageNumber = page;
  update();
}

function openDynamicModal(dynamicModalSettingsInternal: DynamicModal): void {
  modalService.compositionApi.open(
    {
      component: getDynamicComponent(
        dynamicModalSettingsInternal.type as string,
      )! as any,
      width: '98vw',
      canCancel: ['escape', 'outside'],
      props: {
        ...dynamicModalSettingsInternal.propsData,
        widgetDefinition: props.widgetDefinition,
        // TODO: When we migrate to Vue3, we should remove prop (routeParams)
        // and use Router composable in these components
        routeParams: route.value.params,
      },
    },
    app,
  );
}

function getDynamicComponent(type: string): AsyncComponent | undefined {
  switch (type) {
    case 'cut-intraday':
      return defineAsyncComponent(
        () => import('@/components/cut/CutIntraday.vue'),
      );
    case 'detailed-customer-groups':
      return defineAsyncComponent(
        () => import('@/components/sales/DetailedCustomerGroups.vue'),
      );
    case 'detailed-top-sales-people':
      return defineAsyncComponent(
        () => import('@/components/sales/DetailedTopSalespeople.vue'),
      );
    case 'detailed-customer':
      return defineAsyncComponent(
        () => import('@/components/quotes/DetailedCustomerView.vue'),
      );
    case 'cut-states-intraday':
      return defineAsyncComponent(
        () => import('@/components/cut/CutStatesIntraday.vue'),
      );
    case 'oee-intraday':
      return defineAsyncComponent(
        () => import('@/components/cut/OEEIntraday.vue'),
      );
    case 'quotes-customers-expanded-view':
      return defineAsyncComponent(
        () => import('@/components/quotes/QuotesCustomersExpandedView.vue'),
      );
    case 'events-table-expanded-view':
      return defineAsyncComponent(
        () => import('@/components/careConsole/EventsTableExpandedView.vue'),
      );
    case 'modal-wrapped-events-table-expanded-view':
      return defineAsyncComponent(
        () =>
          import(
            '@/components/careConsole/ModalWrappedEventsTableExpandedView.vue'
          ),
      );
  }
}

function onExpandOnDblClick(params: any) {
  const config = props.widgetDefinition.getDblClickModalConfiguration()!;
  openDynamicModal({
    type: config.type,
    propsData: {
      ...filterValues.value,
      ...(config.mapParamsToProps?.(params) ?? {}),
    },
  });
}

function onQuickSearchSelect(selection: QuickSearchItem) {
  if (canExpandOnQuickSearch.value) {
    const config = props.widgetDefinition.getQuickSearchModalConfiguration()!;
    openDynamicModal({
      type: config.type,
      propsData: {
        ...filterValues.value,
        selection,
      },
    });
  }
}

function downloadData() {
  const fileName = i18n.t(`widget.${props.widgetDefinition.widget}`) ?? 'data';
  downloadCsv(data.value, `${fileName}.csv`);
}

function onExpand() {
  const config = props.widgetDefinition.getModalConfiguration()!;
  openDynamicModal({
    type: config.type,
    propsData: {
      ...filterValues.value,
    },
  });
}

async function refreshTableData() {
  await update();
}

// Provided reactively in TechnologyConsoleDevice, where the user selects a customer.
const supportTenantIdDh = computed<number | null>(
  () => injectReactive.supportTenantIdDh ?? null,
);

// Provided reactively in CareConsoleDevice, where the user selects a customer.
const technologyTenantIdDh = computed<number | null>(
  () => injectReactive.technologyTenantIdDh ?? null,
);

const slotWidgetComponent = computed(() => {
  switch (props.widgetDefinition.widget) {
    case WidgetEnum.CutStatus:
      return defineAsyncComponent(
        () => import('@/components/cut/CutStatus.vue'),
      );
    case WidgetEnum.BendStatus:
      return defineAsyncComponent(
        () => import('@/components/bend/BendStatus.vue'),
      );
    case WidgetEnum.TubeStatus:
      return defineAsyncComponent(
        () => import('@/components/tube/TubeStatus.vue'),
      );
    case WidgetEnum.BendingPerformance:
      return defineAsyncComponent(
        () => import('@/components/bend/BendPerformance.vue'),
      );
    case WidgetEnum.BendProductivityFigures:
      return defineAsyncComponent(
        () => import('@/components/bend/BendProductivityFigures.vue'),
      );
    case WidgetEnum.BendStatusOverview:
      return defineAsyncComponent(
        () => import('@/components/bend/BendStatesOverview.vue'),
      );
    case WidgetEnum.BusinessOverviewFigures:
    case WidgetEnum.QuotesFiguresDetailed:
    case WidgetEnum.QuotesFigures:
    case WidgetEnum.SalesFigures:
    case WidgetEnum.ManufacturingFigures:
    case WidgetEnum.TechnologyOverviewSscFigures:
      return defineAsyncComponent(
        () => import('@/components/FiguresWidget.vue'),
      );
    case WidgetEnum.BendShiftStatistics:
      return defineAsyncComponent(
        () => import('@/components/bend/BendShiftStatistics.vue'),
      );
    case WidgetEnum.BendRankingOfParts:
      return defineAsyncComponent(
        () => import('@/components/bend/BendRankingOfParts.vue'),
      );
    default:
      return null;
  }
});

const tableWidgetComponent = computed(() => {
  switch (props.widgetDefinition.widget) {
    case WidgetEnum.LaserIntradayAvailabilityTable:
    case WidgetEnum.TubeIntradayAvailabilityTable:
      return defineAsyncComponent(
        () =>
          import(
            '@/views/workcenterConsole/CutOrTubesIntradayAvailabilityTable/CutOrTubesIntradayAvailabilityTable.vue'
          ),
      );
    case WidgetEnum.BendIntradayAvailabilityTable:
      return defineAsyncComponent(
        () =>
          import(
            '@/views/workcenterConsole/BendIntradayAvailabilityTable/BendIntradayAvailabilityTable.vue'
          ),
      );
    case WidgetEnum.LaserCurrentWorkload:
      return defineAsyncComponent(
        () => import('@/components/cut/CutCurrentWorkloadTable.vue'),
      );
    case WidgetEnum.BendProductionHistory:
      return defineAsyncComponent(
        () => import('@/components/common/BendProductionHistory.vue'),
      );
    default:
      return null;
  }
});

const kpiWidgetComponent = computed(() => {
  switch (props.widgetDefinition.widget) {
    case WidgetEnum.NumberOfBendsWidget:
      return NumberOfBendsWidget;
    case WidgetEnum.PeakHourlyBendingWidget:
      return PeakHourlyBendingWidget;
    case WidgetEnum.BendProductivity:
      return BendProductivityKpiWidget;
    case WidgetEnum.BendProductionOutputWidget:
      return BendProductionOutputWidget;
    default:
      return null;
  }
});

const customerIdDh = computed((): number => {
  if (currentUser().isTechnologyUser) {
    return technologyTenantIdDh.value!;
  } else if (currentUser().isServiceUserType) {
    return supportTenantIdDh.value!;
  } else {
    return customersService.store.currentIdDh()!;
  }
});

const widgetTitle = computed((): string =>
  i18n.t(`widget.${props.widgetDefinition.widget}`).toString(),
);

const canExpandOnQuickSearch = computed((): boolean =>
  props.widgetDefinition.canExpandOnQuickSearch(),
);

const widgetType = computed(
  (): WidgetType => findWidgetType(props.widgetDefinition.widget),
);

const hasWidgetCard = computed(
  (): boolean => ![WidgetType.Table, WidgetType.KPI].includes(widgetType.value),
);

const deviceIds = computed((): string[] => props.widgetDefinition.deviceIds);

const filterValues = computed(() => ({
  selectedShifts: props.widgetDefinition.shifts,
  selectedDeviceIds: deviceIds.value,
  selectedTimeFilter:
    props.widgetDefinition.timeFilter ?? FilterTimeSpanEnum.None,
  selectedDateGrouping: props.widgetDefinition.axisSpan,
  paramValues: paramValues.value,
}));

watch(
  () => props.widgetDefinition,
  async (newValue, oldValue) => {
    initializeParamValuesIfDefined();

    if (newValue.widget !== oldValue.widget) {
      data.value = null;
      isFetchingForTheFirstTime.value = true;
      dataRetriever.value?.abort();
      dataRetriever.value = WidgetFactory.createDataRetriever(
        props.widgetDefinition.widget,
        customerIdDh.value,
      );
      await update();
    } else if (filterChanged(newValue, oldValue)) {
      await update();
    }
  },
);

watch(
  () => [technologyTenantIdDh, supportTenantIdDh],
  async () => {
    await update();
  },
);

onBeforeUnmount(() => {
  dataRetriever.value?.abort();
});

return { __sfc: true,CameraView, Chart, NumberOfBendsWidget, PeakHourlyBendingWidget, BendProductivityKpiWidget, BendProductionOutputWidget, props, emit, i18n, app, route, injectReactive, mapWidgetRef, dataRetriever, data, isFetchingForTheFirstTime, loading, paramValues, getData, update, initializeParamValuesIfDefined, generateParamValuesWithDefaults, hasWidgetDefinitionChanged, onParamUpdated, onPageChange, openDynamicModal, getDynamicComponent, onExpandOnDblClick, onQuickSearchSelect, downloadData, onExpand, refreshTableData, supportTenantIdDh, technologyTenantIdDh, slotWidgetComponent, tableWidgetComponent, kpiWidgetComponent, customerIdDh, widgetTitle, canExpandOnQuickSearch, widgetType, hasWidgetCard, deviceIds, filterValues, WidgetType, WidgetHelp, WidgetCard, MapWidget }
}

})